import {useLayoutEffect, useRef, useState} from "react";
import type {CombatTurnToken as Combatant} from "@/api/TurnApi.ts";
import {useTurn} from "@/encounter/turns/TurnHooks.ts";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import type {Visibility} from "@/tabletop/visibility/VisibilityModel.ts";
import type {AreaEffectMark} from "@/tabletop/effects/AreaEffectHighlights.ts";
import {useTokenPresentation} from "./useTokenPresentation.ts";
import {initiativeArtworkHeight, initiativeLayout, initiativeOrder} from "./InitiativeLayout.ts";
import InitiativePortrait from "./InitiativePortrait.tsx";
import {cn} from "@/lib/utils.ts";
import {useInitiativeAnimation} from "./useInitiativeAnimation.ts";

export type InitiativeBarViewProps = {
    combatants: Combatant[]; activeId?: string; round: number; playerView?: boolean;
    nextRoundCombatants?: Combatant[];
    remainingCombatants?: Combatant[];
    participantCount?: number;
    selectedIds?: string[]; outsideSightIds?: string[]; marks?: Record<string, AreaEffectMark[]>;
    onSelect?: (combatant: Combatant) => void;
};

export function InitiativeBarView({
                                      combatants, nextRoundCombatants, remainingCombatants, participantCount, activeId, round, playerView = false,
                                      selectedIds = [], outsideSightIds = [], marks = {}, onSelect
                                  }: InitiativeBarViewProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    const started = round > 0;
    // Never retain a GM or newly hidden portrait in a transition to a different audience.
    const privacyKey = JSON.stringify([playerView, [...combatants, ...(nextRoundCombatants ?? [])].map(actor => [actor.id, actor.hidden])]);
    useInitiativeAnimation(ref, activeId, round, privacyKey);
    useLayoutEffect(() => {
        const node = ref.current;
        const container = node?.parentElement;
        if (!container) return;
        // Measure the available map width, not this content-sized bar, to avoid resize feedback.
        const measure = () => setWidth(container.getBoundingClientRect().width * 2 / 3);
        measure();
        const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(measure);
        observer?.observe(container);
        window.addEventListener("resize", measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [started]);
    if (round <= 0) return null;
    const {activeIndex, ordered} = initiativeOrder(combatants, activeId, nextRoundCombatants, remainingCombatants, participantCount);
    const count = combatants.length;
    const currentCount = remainingCombatants?.length ?? count - Math.max(0, activeIndex);
    const roundBreakIndex = ordered.length > currentCount && currentCount > 0 ? currentCount : undefined;
    const layout = initiativeLayout(width, ordered.length, roundBreakIndex);
    // Fit the actual artwork and only the detail rows that are visible for this audience.
    const trackHeight = Math.max(layout.roundSize, ...layout.entries.map(entry => {
        const combatant = ordered[entry.index];
        const size = entry.size;
        const artworkHeight = initiativeArtworkHeight(size, entry.stacked);
        if (entry.stacked) return artworkHeight + 4;
        const unknown = playerView && combatant.hidden;
        const marksHeight = !unknown && marks[combatant.id]?.length ? 17 : 0;
        return artworkHeight + marksHeight + 2;
    }));
    const circumference = 2 * Math.PI * 44;
    const segmentLength = count > 0 ? circumference / count : 0;
    const segmentGap = Math.min(3, segmentLength * 0.2);
    const progressLabel = `Runde ${round}, Zug ${activeIndex >= 0 ? activeIndex + 1 : "–"} von ${count}`;

    return <div ref={ref} className={cn(
        "pointer-events-none absolute top-0 left-1/2 z-[5] flex max-w-2/3 -translate-x-1/2 ",
        "items-center overflow-hidden rounded-[16px] border border-[#56617180] bg-[#101923ed] px-[10px] py-[3px]",
        "font-[Verdana,sans-serif] text-[#e8edf4] shadow-[0_8px_24px_#0006]",
        playerView && "select-none")}
                role="region" aria-label="Initiative-Reihenfolge"
                style={{gap: layout.gap, width: 22 + layout.roundSize + layout.gap + layout.trackWidth}}>
        {/* Round progress follows the original initiative order, independent of the rotated portraits. */}
        <svg className="shrink-0 self-center" width={layout.roundSize} height={layout.roundSize} viewBox="0 0 100 100"
             role="img" aria-label={progressLabel}>
            <circle cx="50" cy="50" r="44" fill="none" stroke="#293342" strokeWidth="5"/>
            {combatants.map((combatant, index) =>
                <circle key={combatant.id} cx="50" cy="50" r="44" fill="none"
                        stroke={index === activeIndex ? "#f0c45b" : index < activeIndex ? "#569d88" : "#394556"}
                        strokeWidth={index === activeIndex ? 6 : 4}
                        strokeDasharray={`${segmentLength - segmentGap} ${circumference - segmentLength + segmentGap}`}
                        strokeDashoffset={-index * segmentLength - segmentGap / 2}
                        transform="rotate(-90 50 50)"/>)}
            <text x="50" y="31" textAnchor="middle" fill="#a8b5c5" fontSize="10">RUNDE</text>
            <text x="50" y="57" textAnchor="middle" fill="#f1e6c9" fontSize="28" fontWeight="700">{round}</text>
            <text x="50" y="76" textAnchor="middle" fill="#a8b5c5"
                  fontSize="10">{count > 0 ? `Zug ${activeIndex >= 0 ? activeIndex + 1 : "–"}/${count}` : "Keine Züge"}</text>
        </svg>
        <ol className="relative m-0 min-w-0 shrink-0 list-none p-0"
            style={{width: layout.trackWidth, height: trackHeight}} aria-label="Nächste Züge">
            {layout.entries.map(entry => {
                const combatant = ordered[entry.index];
                const entryRound = entry.index < currentCount ? round : round + 1;
                const active = entryRound === round && combatant.id === activeId;
                const unknown = playerView && combatant.hidden;
                const name = unknown ? "Unbekannt" : combatant.name;
                const portrait = <InitiativePortrait combatant={combatant} size={entry.size}
                                                     active={active} selected={selectedIds.includes(combatant.id)}
                                                     playerView={playerView}
                                                     outsidePlayerSight={outsideSightIds.includes(combatant.id)}
                                                     stacked={entry.stacked}
                                                     marks={unknown ? [] : marks[combatant.id] ?? []}/>;
                return <li key={`${entryRound}:${combatant.id}`} data-combatant-id={combatant.id}
                           data-initiative-key={nextRoundCombatants ? `${entryRound}:${combatant.id}` : undefined}
                           data-round={entryRound} className={cn(
                    "absolute top-0 h-full rounded-[12px] border border-transparent transition-colors duration-[250ms] motion-reduce:transition-none",
                    active && "border-[#e5b54f80]",
                    entry.stacked && "top-[2px] overflow-hidden")}
                           style={{
                               left: entry.left, width: entry.width, zIndex: ordered.length - entry.index,
                               height: entry.stacked ? initiativeArtworkHeight(entry.size, true) + 4 : undefined
                           }}
                           aria-label={`${active ? "Am Zug: " : ""}${name}${entryRound > round ? `, Runde ${entryRound}` : ""}`} aria-current={active ? "step" : undefined}>
                    <span aria-hidden="true" className={cn("pointer-events-none absolute inset-0 -z-10 rounded-[11px] bg-[linear-gradient(#785c252e,#382b131a)] transition-opacity duration-[250ms] motion-reduce:transition-none",
                        active ? "opacity-100" : "opacity-0")}/>
                    {!playerView && !entry.stacked && onSelect
                        ? <button type="button"
                                  className={cn("pointer-events-auto block w-full cursor-pointer rounded-[11px]",
                                      "border-0 bg-transparent p-0 text-inherit [font:inherit] hover:bg-[#a7bed410]",
                                      "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#38bdf8]")}
                                  onClick={() => onSelect(combatant)}
                                  aria-label={`${name} auswählen`}>{portrait}</button>
                        : portrait}
                </li>;
            })}
            {/* Separate the last turn of this round from the first turn of the next, including in a split stack. */}
            {layout.roundBreakLeft !== undefined && <li role="separator" aria-orientation="vertical"
                aria-label={`Beginn Runde ${round + 1}`} className="absolute top-[8px] bottom-[8px] w-px -translate-x-1/2 bg-[#a8b5c5]/70"
                style={{left: layout.roundBreakLeft, zIndex: count + 1}}/>}
            {layout.hiddenCount > 0 &&
                <li className="absolute right-0 bottom-[3px] z-[10000] rounded-[8px] border border-[#536276] bg-[#172231] px-[6px] py-[3px] text-[10px]"
                    aria-label={`${layout.hiddenCount} weitere Combatants`}>+{layout.hiddenCount}</li>}
        </ol>
    </div>;
}

function EncounterInitiativeBar({playerView = false, visibility, selectedIds, onSelect}: {
    playerView?: boolean; visibility?: Visibility; selectedIds?: string[]; onSelect?: (actor: Combatant) => void;
}) {
    const {combatState, initiativeCombatants, nextRoundCombatants, remainingCombatants, activeCombatantId, activeCombatant, round} = useTurn();
    const {marks, outsideSightIds} = useTokenPresentation(visibility);
    return <InitiativeBarView combatants={initiativeCombatants} nextRoundCombatants={nextRoundCombatants}
        remainingCombatants={remainingCombatants} activeId={activeCombatantId ?? activeCombatant?.id} round={round}
        participantCount={combatState?.combatId ? combatState.participants.length : undefined}
        playerView={playerView} marks={marks} outsideSightIds={playerView ? [] : outsideSightIds}
        selectedIds={selectedIds} onSelect={onSelect}/>;
}

function GameMasterInitiativeBar({visibility}: {visibility?: Visibility}) {
    const {selectedCombatants, selectCombatants} = useTableTopSelection();
    return <EncounterInitiativeBar visibility={visibility} selectedIds={selectedCombatants.map(actor => actor.id)}
        onSelect={actor => selectCombatants([actor])}/>;
}

export default function InitiativeBar({playerView = false, visibility}: {playerView?: boolean; visibility?: Visibility}) {
    return playerView ? <EncounterInitiativeBar playerView visibility={visibility}/> : <GameMasterInitiativeBar visibility={visibility}/>;
}
